import { type ButtonHTMLAttributes, forwardRef } from "react";
import { cn } from "@/shared/lib/cn";

type Variant = "primary" | "dark" | "outline" | "ghost";
type Size = "sm" | "md";

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
};

const variantClass: Record<Variant, string> = {
  primary:
    "bg-lime text-[var(--fg-on-accent)] hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)] active:scale-[0.98]",
  dark:
    "bg-ink text-white hover:bg-[#2A2A2A] active:scale-[0.98]",
  outline:
    "bg-transparent text-ink border-2 border-ink hover:bg-ink/5 active:scale-[0.98]",
  ghost:
    "bg-transparent text-ink hover:bg-ink-6",
};

const sizeClass: Record<Size, string> = {
  sm: "px-3 py-1.5 text-[13px] gap-1.5",
  md: "px-5 py-2.5 text-[14px] gap-2",
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = "primary", size = "md", className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center rounded-pill font-display font-semibold whitespace-nowrap transition-[background,box-shadow,transform] duration-100 disabled:opacity-40 disabled:pointer-events-none",
        variantClass[variant],
        sizeClass[size],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
